const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const UUID_IN_TEXT_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;

/** App-wide blank placeholder for missing / unknown display values. */
export const BLANK_DISPLAY = "—";

const UNKNOWN_LABEL_RE =
  /^(unknown(\b.*)?|n\/?a|null|undefined|none|not\s*available|not\s*provided|-|–|--|—)$/i;

export function isUuid(value) {
  return UUID_RE.test(String(value ?? "").trim());
}

export function isIdFieldKey(key) {
  const normalized = String(key ?? "").trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized === "id" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_uuid") ||
    normalized.includes("uuid")
  );
}

export function isIdLikePath(path) {
  const segments = String(path ?? "")
    .split(/[.[\]]+/)
    .filter(Boolean);
  const lastKey = segments[segments.length - 1] || "";
  return isIdFieldKey(lastKey);
}

export function shouldOmitFromDisplay(key, value) {
  if (isIdFieldKey(key)) return true;
  if (isUuid(value)) return true;
  return false;
}

export function formatFieldLabel(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** True when value is empty or a placeholder like Unknown / N/A / -- */
export function isBlankOrUnknownDisplay(value) {
  const text = String(value ?? "").trim();
  if (!text) return true;
  return UNKNOWN_LABEL_RE.test(text);
}

export function sanitizeDisplayValue(value, fallback = BLANK_DISPLAY) {
  if (isBlankOrUnknownDisplay(value)) return fallback;
  const text = String(value ?? "").trim();
  if (isUuid(text)) return fallback;
  return text;
}

export function sanitizeDisplayText(text, fallback = BLANK_DISPLAY) {
  if (text == null || text === "") return fallback;

  let result = String(text).trim();
  if (!result || isBlankOrUnknownDisplay(result)) return fallback;

  result = result
    .replace(
      /[^·\n]*\b(?:id|uuid)\b[^:]*:\s*[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
      "",
    )
    .replace(UUID_IN_TEXT_RE, "")
    .replace(/\s*[·,]\s*(?=[·,])/g, " · ")
    .replace(/^[·,\s]+|[·,\s]+$/g, "")
    .trim();

  if (!result || isBlankOrUnknownDisplay(result)) return fallback;
  return result;
}

export function formatObjectForDisplay(
  details,
  { separator = " · ", allowCredits = true } = {},
) {
  if (details == null || details === "") return BLANK_DISPLAY;
  if (typeof details === "string") {
    return sanitizeDisplayText(details);
  }
  if (typeof details !== "object") {
    return sanitizeDisplayValue(details);
  }

  if (allowCredits && details.credits != null && details.credits !== "") {
    return `Credits: ${details.credits}`;
  }

  const entries = Object.entries(details).filter(
    ([key, value]) => value != null && value !== "" && !shouldOmitFromDisplay(key, value),
  );

  if (!entries.length) return BLANK_DISPLAY;

  const formatted = entries
    .map(([key, value]) => {
      const label = formatFieldLabel(key);
      const displayValue =
        typeof value === "object"
          ? formatObjectForDisplay(value, { separator: ", ", allowCredits: false })
          : sanitizeDisplayValue(value, "");
      if (!displayValue || displayValue === BLANK_DISPLAY) return null;
      return `${label}: ${displayValue}`;
    })
    .filter(Boolean);

  return formatted.length ? formatted.join(separator) : BLANK_DISPLAY;
}

export function redactSensitiveFromValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveFromValue(item));
  }

  if (value && typeof value === "object") {
    const result = {};
    for (const [key, entryValue] of Object.entries(value)) {
      if (shouldOmitFromDisplay(key, entryValue)) continue;
      result[key] = redactSensitiveFromValue(entryValue);
    }
    return result;
  }

  if (isUuid(value)) return "[hidden]";
  return value;
}

export function orgSelectTitle(org) {
  const name = sanitizeDisplayValue(org?.org_name, "");
  if (name) return name;
  return "Unnamed organization";
}
